// Configuration interface for the fluid simulation
export interface FluidConfig {
	// Simulation quality
	SIM_RESOLUTION: number
	DYE_RESOLUTION: number

	// Physics parameters
	DENSITY_DISSIPATION: number
	VELOCITY_DISSIPATION: number
	PRESSURE: number
	PRESSURE_ITERATIONS: number
	CURL: number

	// Interaction
	SPLAT_RADIUS: number
	SPLAT_FORCE: number

	// Visual effects
	SHADING: boolean
	COLORFUL: boolean
	COLOR_UPDATE_SPEED: number
	COLOR_TRANSITION_SPEED: number
	COLOR_MODE: ColorMode
	COLOR: string

	// Control
	PAUSED: boolean
}

// WebGL extension support information
export interface WebGLExtensions {
	formatRGBA: {
		internalFormat: number
		format: number
	} | null
	formatRG: {
		internalFormat: number
		format: number
	} | null
	formatR: {
		internalFormat: number
		format: number
	} | null
	halfFloatTexType: number
	supportLinearFiltering: any
}

// Pointer/interaction tracking
export interface Pointer {
	id: number
	texcoordX: number
	texcoordY: number
	prevTexcoordX: number
	prevTexcoordY: number
	deltaX: number
	deltaY: number
	down: boolean
	moved: boolean
	color: [number, number, number]
}

// Framebuffer object interface
export interface FBO {
	fbo: WebGLFramebuffer
	texture: WebGLTexture
	width: number
	height: number
	texelSizeX: number
	texelSizeY: number
	attach(id: number): number
}

// Double framebuffer for ping-pong rendering
export interface DoubleFBO {
	read: FBO
	write: FBO
	swap(): void
}

// Shader program wrapper
export interface ShaderProgram {
	program: WebGLProgram
	uniforms: Record<string, WebGLUniformLocation>
	bind(): void
}

// Material for shader management
export interface Material {
	program: WebGLProgram
	uniforms: Record<string, WebGLUniformLocation>
	bind(): void
	setKeywords(keywords: string[]): void
}

// Event detail interfaces
export interface FluidSplatEventDetail {
	x: number
	y: number
	velocityX: number
	velocityY: number
	color: [number, number, number]
	force: number
}

export interface FluidReadyEventDetail {
	width: number
	height: number
}

export interface FluidPerformanceEventDetail {
	fps: number
	frameTime: number
}

// Custom events
export interface FluidPointerEventMap {
	"fluid-ready": CustomEvent<FluidReadyEventDetail>
	"fluid-splat": CustomEvent<FluidSplatEventDetail>
	"fluid-interaction-start": CustomEvent<{ x: number; y: number }>
	"fluid-interaction-end": CustomEvent<{ x: number; y: number }>
	"fluid-fps-change": CustomEvent<FluidPerformanceEventDetail>
	"fluid-pause": CustomEvent<{}>
	"fluid-resume": CustomEvent<{}>
}

// Color modes
export type ColorMode = "rainbow" | "monochrome" | "custom"

// Interaction modes
export type InteractionMode = "movement" | "click" | "both"

// Quality presets
export type QualityLevel = "low" | "medium" | "high" | "ultra"

export interface QualityPreset {
	simResolution: number
	dyeResolution: number
	pressureIterations: number
}

export const QUALITY_PRESETS: Record<QualityLevel, QualityPreset> = {
	low: {
		simResolution: 64,
		dyeResolution: 512,
		pressureIterations: 10,
	},
	medium: {
		simResolution: 128,
		dyeResolution: 1024,
		pressureIterations: 20,
	},
	high: {
		simResolution: 256,
		dyeResolution: 2048,
		pressureIterations: 30,
	},
	ultra: {
		simResolution: 512,
		dyeResolution: 4096,
		pressureIterations: 40,
	},
}
